<script setup lang="ts">

import { CARD_COLORS } from '@/enums/common'
import { computed, ref } from 'vue'
import { ECOMMERCE_ITEM_PRICE_TYPE, ECOMMERCE_ITEM_TAG } from '@/types/shared/player-config'
import _10MinsImage from '@/assets/images/prize-cards/10-mins.png'
import _1HourImage from '@/assets/images/prize-cards/1-hour.png'
import _8HoursImage from '@/assets/images/prize-cards/8-hours.png'
import { syncPlayer } from '@/composables/game/player'
import { useEcommerce, useEcommerceItems } from '@/composables/game/ecommerce'
import type { EcommercePrizeCardItem } from '@/types/shop'
import { useConfetti } from '@/composables/game/confetti'
import { sleep } from '@/services/helper'
import { useToaster } from '@/composables/game/toasters'
import { useShop } from '@/composables/game/shop'
import ShopItemDetailsModal from '@/components/modals/ShopItemDetailsModal.vue'
import ShopPurchaseSuccessModal from '@/components/modals/ShopPurchaseSuccessModal.vue'
import BoostCard from '@/components/BoostCard.vue'

const { ecommerceItems } = useEcommerceItems()
const { showConfetti } = useConfetti()
const { notify } = useToaster()
const { getItemDisabledSeconds } = useShop()
const { purchase } = useEcommerce()

const loadingItemId = ref<string | null>(null)

const cardColoursImagesMap = [
  CARD_COLORS.BLUE,
  CARD_COLORS.PINK,
  CARD_COLORS.GREEN,
]

const cardTimeImagesMap = [
  _10MinsImage,
  _1HourImage,
  _8HoursImage,
]

const _ecommerceItems = computed((): EcommercePrizeCardItem[] => ecommerceItems.value
  .filter(item => item.tags.includes(ECOMMERCE_ITEM_TAG.SHOP_BOOSTER))
  .sort(item => item.benefits[0].amount)
  .slice(0, 3)
  .map((item, index) => ({
    ...item,
    disabledSeconds: getItemDisabledSeconds(item),
    color: getCardColour(index),
    timeImage: cardTimeImagesMap[index],
  })),
)

const showItemDetailsModal = ref(false)
const showSuccessModal = ref(false)
const selectedItem = ref<EcommercePrizeCardItem | null>(null)

const cardHeight = ref(0)

function getCardColour(index: number) {
  return cardColoursImagesMap[index % cardColoursImagesMap.length]
}

function isLastEvenItem(index: number) {
  return (index + 1) % 2 === 1 && index === _ecommerceItems.value.length - 1
}

function setLastItemCardHeight(index: number, height: number) {
  if (index > 0) return
  cardHeight.value = height
}

function setSelectedItem(item: EcommercePrizeCardItem) {
  if (item.prices[0].amount === 0) {
    purchaseItem(item)
  } else {
    selectedItem.value = item
    setTimeout(() => showItemDetailsModal.value = true, 50)
  }
}

async function purchaseItem(item: EcommercePrizeCardItem) {
  loadingItemId.value = item.id
  try {
    const { paid } = await purchase({
      item: item,
      priceType: ECOMMERCE_ITEM_PRICE_TYPE.STAR,
      type: 'item',
    })
    if (paid) {
      showConfetti({ spread: 220, count: 90 })
      await syncPlayer()
      loadingItemId.value = null
    } else {
      loadingItemId.value = null
    }
  } catch (e) {
    loadingItemId.value = null
  }
}

function onItemDetailsHide() {
  showItemDetailsModal.value = false
  setTimeout(() => selectedItem.value = null, 50)
}

function onSuccessModalHide() {
  showSuccessModal.value = false
  setTimeout(() => selectedItem.value = null, 50)
}

async function onPurchase() {
  if (!selectedItem.value) return
  showSuccessModal.value = true
  showItemDetailsModal.value = false

  await sleep(250)
  notify({ title: 'Boost activated!', type: 'success' })
  setTimeout(() => showConfetti({ spread: 220, count: 90 }), 100)
  await syncPlayer()
}
</script>

<template>
  <div class="grid grid-cols-2 justify-between gap-6 w-full">
    <BoostCard
      v-for="(item, index) in _ecommerceItems"
      :key="item.id"
      :item="item"
      class="scale-in"
      :class="{'col-span-2': isLastEvenItem(index)}"
      :is-last-even-item="isLastEvenItem(index)"
      :outer-height="isLastEvenItem(index) ? cardHeight : undefined"
      :is-loading="loadingItemId === item.id"
      @height-change="setLastItemCardHeight(index, $event)"
      @click="setSelectedItem(item)"
    />
  </div>

  <ShopItemDetailsModal
    v-if="selectedItem && showItemDetailsModal"
    :item="selectedItem"
    :show="showItemDetailsModal"
    @hide="onItemDetailsHide"
    @on-purchase="onPurchase"
  />
  <ShopPurchaseSuccessModal
    v-if="selectedItem && showSuccessModal"
    :item="selectedItem"
    :show="showSuccessModal"
    @hide="onSuccessModalHide"
  />
</template>

<style scoped lang="scss">
.green-gradient {
  //background: linear-gradient(91.99deg, rgba(126, 187, 0, 0.75) -4.79%, rgba(187, 238, 83, 0.75) 23.5%);
  background: linear-gradient(91.99deg, rgba(53, 159, 127, 0.75) -4.79%, rgba(81, 214, 158, 0.75) 23.5%);
}

.black-gradient::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 12px; /* Ensure the same 12px border-radius */
  padding: 2px; /* Add padding to make the border visible */

  /* Initial gradient state */
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.38) 71.5%);
  opacity: 1; /* Initial opacity */

  /* Clip to border only */
  -webkit-mask: linear-gradient(white 0 0) content-box, linear-gradient(white 0 0);
  mask: linear-gradient(white 0 0) content-box,
  linear-gradient(white 0 0);

  -webkit-mask-composite: xor;
  mask-composite: exclude;

  pointer-events: none; /* Allow interactions with the content */
  z-index: 1; /* Ensure it sits above the image but below the content */
}
</style>
